import { useState, type ReactNode } from 'react';
import { Copy, Info } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { fmtPct } from '@/shared/api/controllers/mpCuotas';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { Moneda } from './MoneyCell';
import { MoneyInput } from './MoneyInput';

/**
 * One editable row per active medio de pago. Kept in the parent's state;
 * the row only renders + emits patches. The parent enforces mutually
 * exclusive `pagoUnico` / `distPagos` flags across rows.
 *
 * When the medio's `categoria` differs from the venta's `moneda`, the
 * `Tipo de Cambio` input opens up — needed to convert between USD and
 * ARS. The "Pesos" column on the `ComprobantePagos` write (and
 * `MontoDolar_P` on Pagos) stores the converted value, despite the
 * literal name.
 */
export type MedioRow = {
  /** ABM_MediosDePago row id — stable key. */
  medioPagoId: string;
  medio: string;
  categoria: 'Local' | 'Internacional';
  /**
   * Medio classification from the catalog (`Tipo_MDP`). Drives the
   * two-Dist auto-distribution rule: when one of the two Dist. rows
   * is Tipo='Efectivo', that row gets the cash portion
   * (`montoCobroEfectivo`) and the other row gets the factura
   * portion (`totalFactura`) instead of the default 50/50 split.
   * Null on legacy catalog rows that pre-date the column.
   */
  tipo: 'Efectivo' | 'Billetera Virtual' | 'Banco' | 'E-cheq' | null;
  pagoUnico: boolean;
  distPagos: boolean;
  tipoCambio: string;
  /** Monto in the medio's native currency. */
  monto: string;
  /** Present when we're editing an existing 03.Pagos row. */
  pagoId?: string;
  /** Caja que admite cuotas (ABM Cajas `Cuotas_MDP`='SI' → MercadoPago). */
  admiteCuotas?: boolean;
  /** Cuotas elegidas (null = un pago, sin recargo). */
  cuotas?: number | null;
  /** % de recargo de esas cuotas (ABM Cuotas MercadoPago, o el snapshot de la venta). */
  recargoPct?: number | null;
};

export type OpcionCuotas = { cuotas: number; porcentaje: number };

/**
 * Nova-routed medios mirror the server-side `isNovaChannelMedio`
 * whitelist (PRs 3.3.c–3.3.e): any medio whose name starts with
 * "Nova " OR is exactly "Herconstru". MX-only — AR has no Nova
 * channels and falls through with everything classified Fornitalia.
 */
function isNovaMedio(name: string): boolean {
  const m = name.trim();
  if (!m) return false;
  if (m.startsWith('Nova ')) return true;
  if (m === 'Herconstru') return true;
  return false;
}

export function MediosDePagoTable({
  rows,
  onRowPatch,
  moneda,
  pendiente,
  total,
  onCopiarMetodos,
  readOnly = false,
  canCopiar,
  guardarSlot,
  pagadoSegunMedios,
  totalCobro,
  ventaEnFlujoNova = false,
  opcionesCuotas = [],
}: {
  rows: MedioRow[];
  onRowPatch: (medioPagoId: string, patch: Partial<MedioRow>) => void;
  moneda: Moneda;
  pendiente: number;
  total: number;
  onCopiarMetodos: () => void;
  readOnly?: boolean;
  canCopiar: boolean;
  /** Optional save action rendered next to "Copiar Métodos" so each card
   *  owns its own save. */
  guardarSlot?: ReactNode;
  /** Sum of currently-checked medios (in venta currency) — drives the
   *  cobertura bar when both this and `totalCobro` are supplied. */
  pagadoSegunMedios?: number;
  /** Venta's full montoTotalCobro (factura + efectivo). Denominator
   *  for the cobertura bar. */
  totalCobro?: number;
  /** `FNova_RO === 'SI'`. Only decides whether Herconstru is labelled as
   *  a Nova channel — off the flow it's a plain MX cash caja. */
  ventaEnFlujoNova?: boolean;
  /** ABM Cuotas MercadoPago (activas): opciones del selector de cuotas. */
  opcionesCuotas?: OpcionCuotas[];
}) {
  const { config: activeCountry } = useActiveCountry();
  // True percentage — NOT clamped, so overshoots (operator entered too
  // much) surface as e.g. 112% in red instead of looking complete.
  const coberturaPct =
    pagadoSegunMedios != null && totalCobro != null && totalCobro > 0
      ? Math.max(0, Math.round((pagadoSegunMedios / totalCobro) * 100))
      : null;
  // Bar width is the only thing that's clamped — visually it caps at
  // 100% so the layout doesn't blow out. The colour + numeric label
  // tell the user when we're past it.
  const coberturaBarPct =
    coberturaPct != null ? Math.min(100, coberturaPct) : null;
  const overCobertura = coberturaPct != null && coberturaPct > 100;

  // Partition rows into Fornitalia / Nova groups for MX. The Nova
  // section gets its own labelled subheader so admins can see at a
  // glance which medios route through the Nova intermediary flow
  // (Anticipo + Factura Interna) vs the direct Fornitalia channels.
  // AR collapses to a single ungrouped list — no Nova channels exist.
  const fornitaliaRows: MedioRow[] = [];
  const novaRows: MedioRow[] = [];
  for (const r of rows) {
    // Herconstru sólo cuenta como canal Nova si la venta entró al flujo:
    // por debajo de `Limite Nova` la cobra Fornitalia directo y ponerla bajo
    // el subtítulo "Nova" haría creer que dispara Anticipo + Factura Interna.
    const esNova =
      isNovaMedio(r.medio) && (ventaEnFlujoNova || r.medio.trim() !== 'Herconstru');
    if (esNova) novaRows.push(r);
    else fornitaliaRows.push(r);
  }
  const showGroups = activeCountry.code === 'MX' && novaRows.length > 0;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border bg-card lg:min-h-0 lg:flex-1">
      <div className="hidden lg:grid lg:grid-cols-[minmax(6rem,1fr),4.5rem,5.5rem,minmax(7rem,9rem),5rem,minmax(8rem,10rem)] lg:gap-2 lg:border-b lg:px-4 lg:py-2.5 lg:text-[11px] lg:font-medium lg:uppercase lg:tracking-wide lg:text-muted-foreground">
        <span>Método</span>
        <span className="text-center">Pago Único</span>
        <span className="text-center">Dist. Pagos</span>
        <span className="text-right">
          Monto {moneda === 'USD' ? activeCountry.currency.primary : 'USD'}
        </span>
        <span className="text-center">Tipo Cambio</span>
        <span className="text-right">Monto</span>
      </div>
      <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {rows.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">
            No hay medios de pago activos cargados.
          </div>
        ) : showGroups ? (
          <>
            {fornitaliaRows.length > 0 && (
              <>
                <GroupHeader label="Fornitalia" />
                {fornitaliaRows.map((r) => (
                  <MedioRowView
                    key={r.medioPagoId}
                    row={r}
                    ventaMoneda={moneda}
                    onPatch={(patch) => onRowPatch(r.medioPagoId, patch)}
                    readOnly={readOnly}
                    opcionesCuotas={opcionesCuotas}
                  />
                ))}
              </>
            )}
            <GroupHeader label="Nova" />
            {novaRows.map((r) => (
              <MedioRowView
                key={r.medioPagoId}
                row={r}
                ventaMoneda={moneda}
                onPatch={(patch) => onRowPatch(r.medioPagoId, patch)}
                readOnly={readOnly}
                opcionesCuotas={opcionesCuotas}
              />
            ))}
          </>
        ) : (
          rows.map((r) => (
            <MedioRowView
              key={r.medioPagoId}
              row={r}
              ventaMoneda={moneda}
              onPatch={(patch) => onRowPatch(r.medioPagoId, patch)}
              readOnly={readOnly}
              opcionesCuotas={opcionesCuotas}
            />
          ))
        )}
      </div>
      <div className="flex flex-col gap-2 border-t bg-muted/30 px-3 py-2.5">
        {/* Two-row footer mirroring the Carga de Pagos card on the
            right: top row is the full-width Cobertura progress
            (label · bar · %); bottom row pairs the Pendiente / Total
            pills with the Copiar Métodos / Guardar action buttons. */}
        {coberturaPct != null && (
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Cobertura
            </span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-200',
                  overCobertura
                    ? 'bg-rose-500'
                    : coberturaPct >= 100
                      ? 'bg-emerald-500'
                      : 'bg-amber-500',
                )}
                style={{ width: `${coberturaBarPct}%` }}
              />
            </div>
            <span
              className={cn(
                'text-xs font-semibold tabular-nums',
                overCobertura
                  ? 'text-rose-700'
                  : coberturaPct >= 100
                    ? 'text-emerald-700'
                    : coberturaPct > 0
                      ? 'text-amber-700'
                      : 'text-muted-foreground',
              )}
            >
              {coberturaPct}%
            </span>
          </div>
        )}
        {/* Mobile: pills on their own row above the buttons (avoids
            the squeeze that clips Pendiente/Total when sidebar +
            phone width are tight). Desktop: buttons + pills + save
            on a single row. */}
        <div className="grid grid-cols-2 gap-2 lg:hidden">
          <FooterPill
            label="Pendiente"
            value={pendiente}
            moneda={moneda}
            tone={Math.abs(pendiente) < 0.01 ? 'neutral' : 'amber'}
          />
          <FooterPill label="Total" value={total} moneda={moneda} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={onCopiarMetodos}
            disabled={readOnly || !canCopiar}
            size="sm"
            className="h-[3.25rem] shrink-0 gap-2 whitespace-nowrap bg-foreground px-3 text-background hover:bg-foreground/90"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
            Copiar Métodos
          </Button>
          <div className="ml-auto hidden flex-1 grid-cols-2 gap-2 lg:grid">
            <FooterPill
              label="Pendiente"
              value={pendiente}
              moneda={moneda}
              tone={Math.abs(pendiente) < 0.01 ? 'neutral' : 'amber'}
            />
            <FooterPill label="Total" value={total} moneda={moneda} />
          </div>
          <div className="ml-auto shrink-0 lg:ml-0">{guardarSlot}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Section separator that splits the medios list into Fornitalia vs Nova
 * groups on MX. Visual style mirrors the "DE NOVA (INTER-COMPAÑÍA)"
 * heading used in Administración → Cuentas por Cobrar so the channel
 * grouping reads consistently across screens.
 */
function GroupHeader({ label }: { label: string }) {
  return (
    <div className="border-b bg-muted/40 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
  );
}

function FooterPill({
  label,
  value,
  moneda,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  moneda: Moneda;
  tone?: 'neutral' | 'amber';
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-xl border bg-secondary px-3 py-2 leading-tight',
        tone === 'amber' && 'border-amber-200 bg-amber-50',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'text-sm font-semibold tabular-nums text-foreground',
          tone === 'amber' && 'text-amber-800',
        )}
      >
        {formatMoney(value, moneda)}
      </span>
    </div>
  );
}

function MedioRowView({
  row,
  ventaMoneda,
  onPatch,
  readOnly,
  opcionesCuotas,
}: {
  row: MedioRow;
  ventaMoneda: Moneda;
  onPatch: (patch: Partial<MedioRow>) => void;
  readOnly: boolean;
  opcionesCuotas: OpcionCuotas[];
}) {
  // Local medios are denominated in the active country's home currency
  // (ARS for AR, MXN for MX). Hardcoding 'ARS' here would tag every MX
  // Local medio as ARS and mis-flag it as cross-currency vs an MXN venta.
  const { config: activeCountry } = useActiveCountry();
  const medioMoneda: Moneda =
    row.categoria === 'Internacional' ? 'USD' : activeCountry.currency.primary;
  const needsTC = medioMoneda !== ventaMoneda;
  // Rows that aren't participating (no checkbox checked) are dimmed and
  // their inputs disabled — users must pick a mode first, then enter the
  // monto. Matches the PowerApps reset-on-uncheck behaviour.
  const selected = row.pagoUnico || row.distPagos;
  const inputsDisabled = readOnly || !selected;
  // Cross-currency rows split the monto editing across two cells:
  //   • Left: editable input in the MEDIO's native currency — that's
  //     `row.monto` directly (the canonical storage shape), so no
  //     conversion math at write time.
  //   • Right ("Monto"): read-only auto-derived VENTA-currency
  //     equivalent. USD medio → row.monto × TC (local); local medio →
  //     row.monto ÷ TC (USD).
  // For non-cross-currency rows the left cell is skipped and "Monto"
  // stays the editable input as before.
  const isMedioUSD = medioMoneda === 'USD';
  const tcValue = parseMoney(row.tipoCambio);
  const tcReady = tcValue != null && tcValue > 0;
  return (
    <div
      className={cn(
        // Mobile: stacked card with labelled cells.
        // Desktop (lg+): the legacy 6-column grid (Método ·
        // PagoÚnico · DistPagos · Monto-local · TC · Monto).
        'flex flex-col gap-3 border-b p-3 transition-opacity last:border-b-0',
        'lg:grid lg:grid-cols-[minmax(6rem,1fr),4.5rem,5.5rem,minmax(7rem,9rem),5rem,minmax(8rem,10rem)] lg:items-center lg:gap-2 lg:px-4 lg:py-1.5',
        !selected && 'opacity-60',
      )}
    >
      {row.admiteCuotas && opcionesCuotas.length > 0 ? (
        <CeldaMedioConCuotas
          row={row}
          selected={selected}
          disabled={inputsDisabled}
          opciones={opcionesCuotas}
          onPatch={onPatch}
        />
      ) : (
        <span
          className={cn(
            'text-sm font-medium',
            selected ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {row.medio}
        </span>
      )}

      {/* Mobile: labelled checkbox row.
          Desktop: `lg:contents` flattens this wrapper so its children
          land directly in the parent grid cells. */}
      <div className="flex items-center justify-around gap-3 rounded-lg bg-muted/40 px-3 py-2 lg:contents lg:bg-transparent lg:p-0">
        <label className="flex items-center gap-2 text-sm lg:contents">
          <CheckboxCell
            checked={row.pagoUnico}
            disabled={readOnly}
            tone="black"
            onChange={(v) => onPatch({ pagoUnico: v })}
          />
          <span className="text-xs uppercase tracking-wide text-muted-foreground lg:hidden">
            Pago Único
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm lg:contents">
          <CheckboxCell
            checked={row.distPagos}
            disabled={readOnly}
            tone="green"
            onChange={(v) => onPatch({ distPagos: v })}
          />
          <span className="text-xs uppercase tracking-wide text-muted-foreground lg:hidden">
            Dist. Pagos
          </span>
        </label>
      </div>

      {/* Desktop-only: editable medio-native monto cell for
          cross-currency rows. The "Monto" column on the right shows
          the venta-currency derivation. Mobile keeps its single
          combined input (the existing "Monto" cell). */}
      <div className="hidden lg:block">
        {needsTC ? (
          <MoneyInput
            variant="filled"
            value={row.monto}
            moneda={medioMoneda}
            disabled={inputsDisabled}
            onChange={(next) => onPatch({ monto: next })}
            className="h-9 w-full text-right"
            aria-label={`Monto en ${medioMoneda} para ${row.medio}`}
          />
        ) : (
          <span className="block h-9 text-center text-sm leading-9 text-muted-foreground">
            —
          </span>
        )}
      </div>

      {/* Mobile: side-by-side Tipo Cambio + Monto with labels above
          each input. Desktop: each cell goes into its own grid track
          via `lg:contents`. */}
      <div className="grid grid-cols-2 gap-2 lg:contents">
        <div className="flex flex-col gap-1 lg:block">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden">
            Tipo Cambio
          </span>
          {needsTC ? (
            <Input
              type="text"
              inputMode="decimal"
              variant="filled"
              value={row.tipoCambio}
              disabled={inputsDisabled}
              onChange={(e) => onPatch({ tipoCambio: sanitizeDecimal(e.target.value) })}
              className="h-9 w-full text-right"
              aria-label={`Tipo de Cambio para ${row.medio}`}
            />
          ) : (
            <span className="block h-9 text-center text-sm leading-9 text-muted-foreground">
              —
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1 lg:block">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden">
            Monto
          </span>
          {needsTC ? (
            // Cross-currency: "Monto" displays the VENTA-currency
            // equivalent. USD medio → monto × TC = local; local medio
            // → monto ÷ TC = USD. Format with the venta moneda so the
            // displayed code (US$, ARS, MXN) matches what the user is
            // covering.
            <span className="block h-9 truncate rounded-md bg-muted/40 px-3 text-right text-sm leading-9 text-muted-foreground tabular-nums">
              {(() => {
                if (!selected) return '—';
                if (!tcReady) return '—';
                const m = parseMoney(row.monto);
                if (m == null) return '—';
                const ventaEquiv = isMedioUSD
                  ? m * (tcValue ?? 1)
                  : m / (tcValue ?? 1);
                return formatMoney(ventaEquiv, ventaMoneda);
              })()}
            </span>
          ) : (
            <MoneyInput
              variant="filled"
              value={row.monto}
              moneda={medioMoneda}
              disabled={inputsDisabled}
              onChange={(next) => onPatch({ monto: next })}
              className="h-9 w-full text-right"
              aria-label={`Monto para ${row.medio}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Strip anything that isn't a digit, dot or comma — lets the user type
 * either locale's decimal separator (es-AR uses `,`; the browser's
 * `type="number"` refuses commas, which is why we switched these inputs
 * to plain text). `parseMoney` normalises both shapes on read.
 */
function sanitizeDecimal(value: string): string {
  // Allow a single leading minus, then digits/separators.
  const cleaned = value.replace(/[^\d.,-]/g, '');
  // Only keep one separator — drop everything after the second.
  const match = cleaned.match(/^-?\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function CheckboxCell({
  checked,
  disabled,
  tone = 'green',
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  /** 'black' = Pago Único (single-method, locked-in), 'green' = Dist. Pagos (split). */
  tone?: 'green' | 'black';
  onChange: (next: boolean) => void;
}) {
  const checkedTone =
    tone === 'black'
      ? 'border-foreground bg-foreground text-background hover:bg-foreground/90'
      : 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600';
  return (
    <div className="flex justify-center">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          checked ? checkedTone : 'border-border bg-background hover:bg-secondary',
        )}
      >
        {checked && (
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

/**
 * Celda "Método" de la caja con cuotas (MercadoPago): al pasar el mouse avisa
 * del recargo por cuotas; abajo, el selector de cuotas (habilitado con la
 * caja tildada). Pedido 2026-10-20.
 */
function CeldaMedioConCuotas({
  row,
  selected,
  disabled,
  opciones,
  onPatch,
}: {
  row: MedioRow;
  selected: boolean;
  disabled: boolean;
  opciones: OpcionCuotas[];
  onPatch: (patch: Partial<MedioRow>) => void;
}) {
  const [detalle, setDetalle] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Tooltip onOpenChange={(o) => !o && setDetalle(false)}>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex cursor-help items-center gap-1 text-sm font-medium',
              selected ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {row.medio}
            <Info className="h-4 w-4 shrink-0 text-foreground" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">Pago en cuotas con recargo</p>
          <p className="mt-0.5 text-xs leading-snug">
            Si el cliente paga en cuotas, se suma un recargo sobre el total con factura (un
            solo pago).
          </p>
          {detalle ? (
            <>
              <ul className="mt-1 space-y-0.5 text-xs">
                {opciones.map((o) => (
                  <li key={o.cuotas} className="flex justify-between gap-4">
                    <span>{o.cuotas} cuotas</span>
                    <span className="font-semibold tabular-nums">+{fmtPct(o.porcentaje)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <button
              type="button"
              className="mt-1 text-xs font-medium underline underline-offset-2"
              onClick={() => setDetalle(true)}
            >
              Ver detalle
            </button>
          )}
        </TooltipContent>
      </Tooltip>
      {/* Sólo con la caja tildada: sin tildar no lleva recargo. */}
      {selected && (
        <Select
          value={row.cuotas ? String(row.cuotas) : '1'}
          disabled={disabled}
          onValueChange={(v) => {
            const n = Number(v);
            const op = opciones.find((o) => o.cuotas === n);
            if (!op && n === row.cuotas) return; // el snapshot, re-elegido
            onPatch(op ? { cuotas: op.cuotas, recargoPct: op.porcentaje } : { cuotas: null, recargoPct: null });
          }}
        >
          <SelectTrigger className="h-7 w-[6.5rem] gap-1 px-2 text-xs" aria-label={`Cuotas para ${row.medio}`}>
            {/* Corto: la columna Método es angosta; el % está en el menú y en Totales. */}
            <SelectValue>{row.cuotas ? `${row.cuotas} cuotas` : 'Sin cuotas'}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Sin cuotas</SelectItem>
            {opciones.map((o) => (
              <SelectItem key={o.cuotas} value={String(o.cuotas)}>
                {o.cuotas} cuotas · +{fmtPct(o.porcentaje)}
              </SelectItem>
            ))}
            {/* Snapshot de la venta que ya no está en el ABM: se sigue mostrando. */}
            {row.cuotas && !opciones.some((o) => o.cuotas === row.cuotas) && (
              <SelectItem value={String(row.cuotas)}>
                {row.cuotas} cuotas · +{fmtPct(row.recargoPct ?? 0)}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
